import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import PostCard from '../components/post/PostCard';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Settings, Grid3X3, Bookmark, Heart, BadgeCheck, Calendar, Link as LinkIcon, Crown, DollarSign, Sparkles, MapPin, Camera } from 'lucide-react';
import { format } from 'date-fns';
import TipModal from '../components/monetization/TipModal';
import SubscribeModal from '../components/monetization/SubscribeModal';
import FollowListModal from '../components/social/FollowListModal';
import { resolveMedia } from '../components/social/discovery';
import ImageEditorModal from '../components/post/ImageEditorModal';
import MediaLightbox from '../components/post/MediaLightbox';

const ALLOWED_IMG = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_AVATAR = 10 * 1024 * 1024;   // 10MB
const MAX_COVER = 15 * 1024 * 1024;    // 15MB

const validateImage = (file, maxSize) => {
  if (!ALLOWED_IMG.includes(file.type)) return 'Please choose a JPG, PNG or WEBP image.';
  if (file.size > maxSize) return `Image is too large (max ${Math.round(maxSize / 1024 / 1024)}MB).`;
  return null;
};

export default function ProfilePage() {
  const { username } = useParams();
  const { user: currentUser, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [followModalOpen, setFollowModalOpen] = useState(false);
  const [followModalTab, setFollowModalTab] = useState('followers');

  const openFollowModal = (tab) => { setFollowModalTab(tab); setFollowModalOpen(true); };

  // Profile media editing (avatar + cover)
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  // Choose → adjust (ImageEditorModal) → save: { mode: 'avatar' | 'banner', file }
  const [imageEditor, setImageEditor] = useState(null);
  const [viewImage, setViewImage] = useState(null); // full-screen viewer for others' avatar/banner

  const isOwnProfile = currentUser?.username === username;

  const pickAvatar = () => avatarInputRef.current?.click();
  const pickCover = () => coverInputRef.current?.click();

  const onSelectImage = (e, maxSize, mode) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const err = validateImage(file, maxSize);
    if (err) { toast.error(err); return; }
    setImageEditor({ mode, file });
  };

  // The editor has uploaded the adjusted image; store it on the profile.
  const applyEditedImage = async (url) => {
    const field = imageEditor?.mode === 'avatar' ? 'avatar' : 'cover_image';
    try {
      const res = await api.put('/users/profile', { [field]: url });
      setProfile(res.data);
      if (isOwnProfile) updateUser({ [field]: url });
      toast.success(field === 'avatar' ? 'Profile picture updated' : 'Banner updated');
      setImageEditor(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the image');
    }
  };


  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      setLoading(true);
      let profileData = null;
      // Primary: the profile itself. Only THIS failing means "User not found".
      try {
        const profileRes = await api.get(`/users/username/${username}`);
        profileData = profileRes.data;
        if (!cancelled) setProfile(profileData);
      } catch (error) {
        console.error('Error fetching profile:', error);
        if (!cancelled) { setProfile(null); toast.error('User not found'); setLoading(false); }
        return;
      }
      if (!cancelled) setLoading(false);

      // Secondary data — failures here must NOT blank the profile.
      try {
        const postsRes = await api.get(`/posts/user/${username}`);
        if (!cancelled) setPosts(postsRes.data);
      } catch (e) {
        console.error('Error fetching posts:', e);
      }

      if (!isOwnProfile && profileData?.id) {
        try {
          const followRes = await api.get(`/users/${profileData.id}/is-following`);
          if (!cancelled) setIsFollowing(followRes.data.is_following);
        } catch (e) { /* non-fatal */ }
        try {
          const subRes = await api.get(`/subscriptions/check/${profileData.id}`);
          if (!cancelled) setSubscriptionStatus(subRes.data);
        } catch (e) { /* non-fatal */ }
      }
    };

    fetchProfile();
    return () => { cancelled = true; };
  }, [username, isOwnProfile]);

  const handleFollow = async () => {
    if (!profile) return;
    setFollowLoading(true);

    try {
      if (isFollowing) {
        await api.delete(`/users/${profile.id}/follow`);
        setIsFollowing(false);
        setProfile(prev => ({ ...prev, followers_count: prev.followers_count - 1 }));
        toast.success(`Unfollowed @${profile.username}`);
      } else {
        await api.post(`/users/${profile.id}/follow`);
        setIsFollowing(true);
        setProfile(prev => ({ ...prev, followers_count: prev.followers_count + 1 }));
        toast.success(`Following @${profile.username}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const handlePostUpdate = (postId, updates) => {
    setPosts(prev => prev.map(post => 
      post.id === postId ? { ...post, ...updates } : post
    ));
  };

  const handlePostDelete = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
    if (isOwnProfile) {
      updateUser({ posts_count: currentUser.posts_count - 1 });
    }
  };

  if (loading) {
    return (
      <div className="mg-page max-w-4xl" data-testid="profile-page">
        <div className="pt-6">
          <Skeleton className="h-48 w-full rounded-3xl mb-4" />
          <div className="flex items-end gap-6 -mt-16 px-6">
            <Skeleton className="w-32 h-32 rounded-full" />
            <div className="flex-1 pt-16 space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="mg-page max-w-4xl text-center py-20" data-testid="profile-page">
        <h2 className="font-heading text-2xl font-bold mb-2">User not found</h2>
        <p className="text-muted-foreground">The user you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="mg-page max-w-5xl" data-testid="profile-page">
      {/* Cover: banners are saved at 3:1 and shown in full from 640px; phones show the
          centre at 5:2, edge to edge. No overlay: the member's image stays as uploaded. */}
      <div className="relative -mx-3 sm:mx-0 aspect-[5/2] sm:aspect-[3/1] rounded-none sm:rounded-3xl overflow-hidden bg-gradient-to-br from-primary/30 to-accent/30 sm:mt-6" data-testid="profile-cover">
        {profile.cover_image && (
          isOwnProfile ? (
            <img src={resolveMedia(profile.cover_image)} alt="" className="w-full h-full object-cover" />
          ) : (
            <button type="button" className="block w-full h-full cursor-zoom-in" onClick={() => setViewImage(resolveMedia(profile.cover_image))}
              aria-label="View banner" data-testid="view-banner">
              <img src={resolveMedia(profile.cover_image)} alt="" className="w-full h-full object-cover" />
            </button>
          )
        )}

        {isOwnProfile && (
          <>
            <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onSelectImage(e, MAX_COVER, 'banner')} data-testid="cover-file-input" />
            <button
              onClick={pickCover}
              className="absolute top-3 right-3 flex items-center gap-2 px-3 py-2 rounded-full bg-black/55 text-white text-sm backdrop-blur hover:bg-black/75 transition-colors"
              data-testid="edit-cover-btn"
              aria-label="Change banner"
            >
              <Camera className="w-4 h-4" /> <span className="hidden sm:inline">Change Banner</span>
            </button>
          </>
        )}
      </div>

      {/* Profile Info */}
      <div className="relative px-1 sm:px-4 md:px-6 pb-6">
        {/* Avatar overlaps the banner edge; name always sits below the banner.
            Phones: avatar, name, then a full-width action grid.
            Tablet up: actions share the avatar row, right-aligned. */}
        <div className="flex flex-wrap items-end gap-x-4">
          <div className="relative order-1 w-24 h-24 sm:w-28 sm:h-28 md:w-36 md:h-36 shrink-0 -mt-12 sm:-mt-14 md:-mt-20" data-testid="profile-avatar">
            <Avatar className="w-full h-full border-4 border-background ring-4 ring-primary/30">
              <AvatarImage src={resolveMedia(profile.avatar)} alt={profile.display_name} className="object-cover" />
              <AvatarFallback className="text-4xl">{profile.display_name?.[0]}</AvatarFallback>
            </Avatar>
            {!isOwnProfile && profile.avatar && (
              <button type="button" className="absolute inset-0 rounded-full cursor-zoom-in" onClick={() => setViewImage(resolveMedia(profile.avatar))}
                aria-label="View profile picture" data-testid="view-avatar" />
            )}
            {isOwnProfile && (
              <>
                <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onSelectImage(e, MAX_AVATAR, 'avatar')} data-testid="avatar-file-input" />
                {/* Entire avatar is pressable to change the picture */}
                <button
                  onClick={pickAvatar}
                  className="absolute inset-0 rounded-full group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  data-testid="avatar-press"
                  aria-label="Change profile picture"
                >
                  <span className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/35 transition-colors flex items-center justify-center">
                    <Camera className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </button>
                <span
                  className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center shadow-lg pointer-events-none"
                  data-testid="edit-avatar-btn"
                  aria-hidden="true"
                >
                  <Camera className="w-4 h-4" />
                </span>
              </>
            )}
          </div>

          <div className="contents">
            <div className="order-2 md:order-3 basis-full min-w-0 pt-3">
              <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-x-2 gap-y-1 flex-wrap break-words [overflow-wrap:anywhere]">
                {profile.display_name}
                {profile.is_verified && <BadgeCheck className="w-6 h-6 text-primary fill-primary/20" />}
                {profile.is_npc && (
                  <span className="mg-npc-badge" data-testid="profile-npc-badge">
                    <Sparkles className="w-3 h-3" aria-hidden="true" />
                    Myth
                  </span>
                )}
              </h1>
              <p className="text-muted-foreground">@{profile.username}</p>
              {(profile.title || profile.realm) && (
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {profile.title && (
                    <span className="text-sm font-medium text-mg-purple" data-testid="profile-title">{profile.title}</span>
                  )}
                  {profile.realm && (
                    <span className="mg-realm-badge" data-testid="profile-realm-badge">
                      <MapPin className="w-3 h-3" aria-hidden="true" />
                      {profile.realm}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="order-3 md:order-2 basis-full md:basis-auto md:ml-auto grid grid-cols-2 md:flex md:flex-wrap md:justify-end gap-2 md:gap-3 pt-4 md:pt-0 md:pb-1 [&>a]:min-w-0 [&_button]:w-full md:[&_button]:w-auto">
              {isOwnProfile ? (
                <>
                  <Link to="/settings">
                    <Button variant="outline" className="rounded-full" data-testid="edit-profile-btn">
                      <Settings className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  </Link>
                  <Link to="/creator-dashboard">
                    <Button variant="outline" className="rounded-full" data-testid="creator-dashboard-btn">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Earnings
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`rounded-full ${
                      isFollowing 
                        ? 'bg-muted text-foreground hover:bg-destructive hover:text-white' 
                        : 'bg-primary text-white'
                    }`}
                    data-testid="follow-btn"
                  >
                    {followLoading ? (
                      <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    ) : (
                      isFollowing ? 'Following' : 'Follow'
                    )}
                  </Button>
                  <Button 
                    onClick={() => setShowTipModal(true)}
                    variant="outline" 
                    className="rounded-full"
                    data-testid="tip-btn"
                  >
                    <Heart className="w-4 h-4 mr-2 text-red-500" />
                    Tip
                  </Button>
                  {subscriptionStatus?.is_subscribed ? (
                    <Button variant="outline" className="rounded-full" disabled>
                      <Crown className="w-4 h-4 mr-2 text-yellow-500" />
                      Subscribed
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => setShowSubscribeModal(true)}
                      className="rounded-full bg-gradient-to-r from-primary to-accent"
                      data-testid="subscribe-btn"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Subscribe
                    </Button>
                  )}
                  <Link to={`/messages?user=${profile.id}`}>
                    <Button variant="outline" className="rounded-full" data-testid="message-btn">
                      Message
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bio */}
        {profile.bio && (
          <p className="mt-4 text-foreground max-w-xl">{profile.bio}</p>
        )}

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Joined {format(new Date(profile.created_at), 'MMMM yyyy')}
          </span>
        </div>

        {/* Stats */}
        <div className="flex gap-2 sm:gap-3 mt-6" data-testid="profile-stats">
          <div className="text-center px-3 py-2 rounded-2xl" data-testid="stat-posts">
            <p className="font-heading text-xl sm:text-2xl font-bold leading-tight">{profile.posts_count}</p>
            <p className="text-xs sm:text-sm text-muted-foreground">Posts</p>
          </div>
          <button
            onClick={() => openFollowModal('followers')}
            className="text-center px-3 py-2 rounded-2xl hover:bg-muted/60 transition-colors"
            data-testid="stat-followers"
          >
            <p className="font-heading text-xl sm:text-2xl font-bold leading-tight">{profile.followers_count}</p>
            <p className="text-xs sm:text-sm text-muted-foreground">Followers</p>
          </button>
          <button
            onClick={() => openFollowModal('following')}
            className="text-center px-3 py-2 rounded-2xl hover:bg-muted/60 transition-colors"
            data-testid="stat-following"
          >
            <p className="font-heading text-xl sm:text-2xl font-bold leading-tight">{profile.following_count}</p>
            <p className="text-xs sm:text-sm text-muted-foreground">Following</p>
          </button>
          <button
            onClick={() => openFollowModal('friends')}
            className="text-center px-3 py-2 rounded-2xl hover:bg-muted/60 transition-colors"
            data-testid="stat-friends"
          >
            <p className="font-heading text-xl sm:text-2xl font-bold leading-tight">{profile.friends_count ?? 0}</p>
            <p className="text-xs sm:text-sm text-muted-foreground">Friends</p>
          </button>
        </div>
      </div>

      <FollowListModal
        open={followModalOpen}
        onOpenChange={setFollowModalOpen}
        userId={profile.id}
        username={profile.username}
        initialTab={followModalTab}
        counts={{ followers: profile.followers_count, following: profile.following_count, friends: profile.friends_count ?? 0 }}
      />

      {/* Content Tabs */}
      <Tabs defaultValue="posts" className="mt-4">
        <TabsList className="w-full max-w-sm bg-muted/50 p-1 rounded-full">
          <TabsTrigger 
            value="posts" 
            className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
            data-testid="profile-posts-tab"
          >
            <Grid3X3 className="w-4 h-4 mr-2" />
            Posts
          </TabsTrigger>
          <TabsTrigger 
            value="media" 
            className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
            data-testid="profile-media-tab"
          >
            <Heart className="w-4 h-4 mr-2" />
            Media
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-6">
          <div className="space-y-6 pb-8">
            {posts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No posts yet</p>
              </div>
            ) : (
              posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  onUpdate={handlePostUpdate}
                  onDelete={handlePostDelete}
                />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="media" className="mt-6">
          <div className="grid grid-cols-3 gap-2 pb-8">
            {posts.filter(p => p.media_urls?.length > 0).map(post => (
              <Link
                key={post.id}
                to={`/post/${post.id}`}
                className="aspect-square rounded-xl overflow-hidden bg-muted"
              >
                <img 
                  src={resolveMedia(post.media_urls[0])} 
                  alt=""
                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                />
              </Link>
            ))}
            {posts.filter(p => p.media_urls?.length > 0).length === 0 && (
              <div className="col-span-3 text-center py-12">
                <p className="text-muted-foreground">No media posts yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Monetization Modals */}
      <TipModal 
        open={showTipModal} 
        onOpenChange={setShowTipModal} 
        creator={profile} 
      />
      <SubscribeModal 
        open={showSubscribeModal} 
        onOpenChange={setShowSubscribeModal} 
        creator={profile} 
      />

      <MediaLightbox open={!!viewImage} items={viewImage ? [{ type: 'image', url: viewImage }] : []} onClose={() => setViewImage(null)} />

      {isOwnProfile && (
        <ImageEditorModal
          open={!!imageEditor}
          mode={imageEditor?.mode}
          file={imageEditor?.file}
          onClose={() => setImageEditor(null)}
          onSave={applyEditedImage}
        />
      )}
    </div>
  );
}
